/**
 * Page 3 right, below Kalenderjahr and Rollierend: one image instead of three.
 *
 * SQUAD decided in the weekly of 30.10.2026 to replace the three slots (Historische
 * Asset-Allokation, Investmentprozess, Zusammenfassung) with a single upload that carries
 * the navy section headers itself. The space it gets varies with the data: the
 * Kalenderjahr table grows by a row pair every seven years, and tranches with a shorter
 * history have fewer. So the image takes whatever is left down to the page-3 foot note —
 * full column width while it fits, scaled down with its aspect kept when it does not —
 * and the PDF preview then names the height that would have fitted ("dann kommt halt der
 * Hinweis, in dem Fall ist das Bild zu groß", Alex).
 *
 * The form names that height before the upload ("Gern schon direkt beim Upload",
 * Benjamin 30.10.2026), estimated from the document data — see
 * estimateSquadPage3ImageArea. The PDF preview measures it and stays the authority.
 *
 * Every live document still carries the three old images. They stay in print until SQUAD
 * uploads the combined one, so no factsheet changes on deploy.
 */
import { kalenderjahrHasData, normalizeKalenderjahrData } from './kalenderjahr';
import {
  SQUAD_PAGE_HEADER,
  SQUAD_SECTION_GEOMETRY_PDF,
  SQUAD_PDF_MAIN_WIDTH_PT,
  SQUAD_TYPOGRAPHY_PDF,
  squadPdfBodyTop,
} from './pageHeaderTokens';
import { SEGOE_UI_LINE_HEIGHT, squadNoteLineCount } from './squadSegoeUiMetrics';
import { SQUAD_ROLLING_CHART_HEIGHT_PDF_PT } from './squadPortfolioChartUtils';

/** Width SQUAD supplies every image in (see squadImageFormats); the hints count in it. */
export const SQUAD_PAGE3_IMAGE_WIDTH_PX = 1280;

export type SquadPage3ImageLayout =
  /** The combined image (may be empty: then nothing is printed). */
  | { kind: 'single'; imageUrl: string }
  /** The three old slots with their navy bars, exactly as before 30.10.2026. */
  | { kind: 'legacy' };

const filled = (value: unknown) => typeof value === 'string' && value.trim() !== '';

/** Field keys of the three slots the combined image replaces. */
export const SQUAD_PAGE3_LEGACY_IMAGE_KEYS = [
  'historischeAssetAllokationImageUrl',
  'investmentprozessImageUrl',
  'zusammenfassungImageUrl',
] as const;

/**
 * The one place that decides what fills page 3 below Rollierend. All three renderers
 * and the form ask here, so they cannot drift apart.
 *
 * It works on the mapped document rather than being a field the mapper fills in once,
 * because the editor keeps editing that object: the combined image uploaded in the form
 * has to replace the old three in the preview straight away, and clearing it again has
 * to bring them back.
 */
export function resolveSquadPage3Layout(data: Record<string, unknown> | null | undefined): SquadPage3ImageLayout {
  const imageUrl = typeof data?.page3ImageUrl === 'string' ? data.page3ImageUrl.trim() : '';
  if (imageUrl) return { kind: 'single', imageUrl };
  if (SQUAD_PAGE3_LEGACY_IMAGE_KEYS.some((key) => filled(data?.[key]))) return { kind: 'legacy' };
  return { kind: 'single', imageUrl: '' };
}

export type SquadPage3ImageArea = { widthPt: number; heightPt: number };
export type SquadImageSize = { width: number; height: number };

/** A4 height (pt). */
const PDF_A4_HEIGHT_PT = 841.89;

/**
 * Page 3's main column as FactsheetPDF (PdfPreviewDialog) lays it out, in pt — the parts
 * not already in pageHeaderTokens. squadPage3Image.test.ts holds them against the
 * renderer and the sum against the generated PDFs.
 */
export const SQUAD_PAGE3_PDF_GEOMETRY = {
  /** styles.mainCol `gap`: between Kalenderjahr, Rollierend, the image and the foot note. */
  columnGap: 5,
  /** styles.navyBar `marginBottom`. */
  navyBarMarginBottom: 5,
  /** KalenderjahrTable: styles.tableCell padding above and below, a hairline under each row and over the first. */
  tableCellPadding: 3,
  tableHairline: 0.5,
  /** KalenderjahrTable without data: the 48 pt placeholder box and styles.placeholder's 4 pt above and below. */
  tablePlaceholder: 56,
  /** Rollierend: the chart, below the bar. */
  rollingChartMarginTop: 4,
  rollingChart: SQUAD_ROLLING_CHART_HEIGHT_PDF_PT,
  /** FootnoteLines under Kalenderjahr and Rollierend; the page-3 foot note. */
  noteMarginTop: 3,
  footerNoteMarginTop: 4,
} as const;

/** The document fields that move the image's lower and upper edge. */
export type SquadPage3FitInput = {
  kalenderjahrPerformance?: unknown;
  kalenderjahrFootnote?: unknown;
  rollingPerformanceFootnote?: unknown;
  page3FooterNote?: unknown;
};

/**
 * The image area on page 3 as the PDF will lay it out, from the document data alone —
 * what the form shows before anything is uploaded.
 *
 * It walks the main column top to bottom like FactsheetPDF: the header (its height
 * follows the title size, see squadTitleFit), Kalenderjahr with one 17.14 pt row per
 * table row, Rollierend, then the foot note from the bottom up. Notes are 6 pt lines,
 * wrapped as the PDF wraps them (squadNoteLineCount).
 *
 * It assumes the rolling chart is there; without it the PDF has 4 pt more. Notes wrap
 * at worst a line earlier than in the PDF. So it errs low, never high: an image made to
 * this height is not shrunk.
 */
export function estimateSquadPage3ImageArea(
  data: SquadPage3FitInput | null | undefined,
  titleFontSize: number = SQUAD_PAGE_HEADER.titleFontSize,
): SquadPage3ImageArea {
  const G = SQUAD_PAGE3_PDF_GEOMETRY;
  const widthPt = SQUAD_PDF_MAIN_WIDTH_PT;
  const lineHeight = (fontSizePt: number) => fontSizePt * SEGOE_UI_LINE_HEIGHT;
  const noteLines = (text: unknown) => squadNoteLineCount(text, widthPt, SQUAD_TYPOGRAPHY_PDF.note);
  const note = (text: unknown) => {
    const lines = noteLines(text);
    return lines ? G.noteMarginTop + lines * lineHeight(SQUAD_TYPOGRAPHY_PDF.note) : 0;
  };

  const navyBar =
    SQUAD_SECTION_GEOMETRY_PDF.navyBarMarginTop +
    SQUAD_SECTION_GEOMETRY_PDF.navyBarPaddingTop +
    lineHeight(SQUAD_TYPOGRAPHY_PDF.navyBar) +
    SQUAD_SECTION_GEOMETRY_PDF.navyBarPaddingBottom +
    G.navyBarMarginBottom;
  const table = kalenderjahrHasData(data?.kalenderjahrPerformance)
    ? normalizeKalenderjahrData(data?.kalenderjahrPerformance).values.length *
        (lineHeight(SQUAD_TYPOGRAPHY_PDF.tableCell) + 2 * G.tableCellPadding + G.tableHairline) +
      G.tableHairline
    : G.tablePlaceholder;
  const kalenderjahr = navyBar + table + note(data?.kalenderjahrFootnote);
  const rollierend = navyBar + G.rollingChartMarginTop + G.rollingChart + note(data?.rollingPerformanceFootnote);

  // The foot note's box is there whenever the field is set, even if it prints no line
  const footerLines = noteLines(data?.page3FooterNote);
  const footer = data?.page3FooterNote
    ? G.columnGap + (footerLines ? G.footerNoteMarginTop + footerLines * lineHeight(SQUAD_TYPOGRAPHY_PDF.note) : 0)
    : 0;

  const top =
    squadPdfBodyTop(titleFontSize) +
    kalenderjahr +
    G.columnGap +
    rollierend +
    G.columnGap +
    SQUAD_SECTION_GEOMETRY_PDF.navyBarMarginTop;
  const bottom = PDF_A4_HEIGHT_PT - SQUAD_PAGE_HEADER.pdfPagePaddingBottom - footer;
  return { widthPt, heightPt: Math.max(0, bottom - top) };
}

/** Below this the area is gone in practice — the tables above already fill the page. */
const MIN_AREA_PT = 1;

/** Height in px at SQUAD_PAGE3_IMAGE_WIDTH_PX that fits the area, rounded down. */
export function squadPage3MaxHeightPx(area: SquadPage3ImageArea): number {
  if (!(area.widthPt > 0) || !(area.heightPt > 0)) return 0;
  return Math.floor((area.heightPt / area.widthPt) * SQUAD_PAGE3_IMAGE_WIDTH_PX);
}

/**
 * Slack on both sides of the space, 5 % of its height — about 20 pt (7 mm) on the
 * Value master.
 *
 * Below: that much white under the image still counts as fitting.
 * Above: the PDF does shrink the image, but only a little — and the form's estimate errs
 * low by up to a line of notes (~28 px), so there such an image may well fit. Both get a
 * softer word than a clear miss.
 */
export const SQUAD_PAGE3_FIT_TOLERANCE = 0.05;

export type SquadPage3ImageFit =
  | { kind: 'no-room' }
  | {
      kind: 'too-tall' | 'slightly-tall' | 'fits' | 'too-short';
      /** Height that fits at 1280 px width, rounded down. */
      maxHeightPx: number;
      /** The image's height at 1280 px width. */
      imageHeightPx: number;
      /** Supplied at another width, so imageHeightPx is not its own pixel height. */
      rescaled: boolean;
      /** Size it is drawn at in the PDF, in % (100 unless taller than the space). */
      scalePct: number;
      /** White left under the image in the PDF (pt). */
      emptyPt: number;
    };

/**
 * The one decision on how an image fits the area — the form (estimated area) and the
 * PDF preview (measured area) both word theirs from it, so they cannot disagree on
 * the same numbers.
 *
 * Compared as shapes, not pixels: a narrower upload is stretched to the column width
 * just the same, so what matters is its height at 1280 px.
 */
export function squadPage3ImageFit(
  area: SquadPage3ImageArea | null | undefined,
  image: SquadImageSize | null | undefined,
): SquadPage3ImageFit | null {
  if (!area || !image || !(image.width > 0) || !(image.height > 0)) return null;
  if (!(area.widthPt > 0) || area.heightPt < MIN_AREA_PT) return { kind: 'no-room' };
  const imageHeightPx = (image.height / image.width) * SQUAD_PAGE3_IMAGE_WIDTH_PX;
  const fitsHeightPx = (area.heightPt / area.widthPt) * SQUAD_PAGE3_IMAGE_WIDTH_PX;
  const facts = {
    maxHeightPx: squadPage3MaxHeightPx(area),
    imageHeightPx: Math.round(imageHeightPx),
    rescaled: image.width !== SQUAD_PAGE3_IMAGE_WIDTH_PX,
  };
  if (imageHeightPx > fitsHeightPx + 0.5) {
    const scale = fitsHeightPx / imageHeightPx;
    const kind = scale >= 1 - SQUAD_PAGE3_FIT_TOLERANCE ? 'slightly-tall' : 'too-tall';
    return { kind, ...facts, scalePct: Math.floor(scale * 100), emptyPt: 0 };
  }
  const emptyPt = area.heightPt - (imageHeightPx / SQUAD_PAGE3_IMAGE_WIDTH_PX) * area.widthPt;
  const kind = imageHeightPx < fitsHeightPx * (1 - SQUAD_PAGE3_FIT_TOLERANCE) ? 'too-short' : 'fits';
  return { kind, ...facts, scalePct: 100, emptyPt: Math.max(0, emptyPt) };
}

const NO_ROOM_TEXT = 'Unter „Rollierende Wertentwicklung“ ist kein Platz mehr – das Bild fehlt im PDF.';

/** "max. 1417 px Höhe bei 1280 px Breite (das Bild hat 1600 px)" — shared by dialog and form. */
const spaceText = (fit: { maxHeightPx: number; imageHeightPx: number; rescaled: boolean }, approx: string) =>
  `${approx}${fit.maxHeightPx} px Höhe bei ${SQUAD_PAGE3_IMAGE_WIDTH_PX} px Breite ` +
  `(das Bild hat ${fit.rescaled ? 'umgerechnet ' : ''}${fit.imageHeightPx} px)`;

export type SquadPage3ImageStatus = { severity: 'success' | 'info' | 'warning'; text: string };

/**
 * Notice for the preview dialog, `null` while the image fits at full width. Uses the area
 * the dialog measured in the real PDF, so it states what happened, not what may happen.
 */
export function squadPage3ImageWarning(
  area: SquadPage3ImageArea | null | undefined,
  image: SquadImageSize | null | undefined,
): SquadPage3ImageStatus | null {
  const fit = squadPage3ImageFit(area, image);
  if (!fit) return null;
  if (fit.kind === 'no-room') return { severity: 'warning', text: `Seite 3: ${NO_ROOM_TEXT}` };
  const space = `Platz in diesem Factsheet: ${spaceText(fit, 'max. ')}.`;
  if (fit.kind === 'slightly-tall') {
    return {
      severity: 'info',
      text: `Seite 3: Das Bild ist etwas zu hoch und wurde leicht verkleinert, auf ${fit.scalePct} %. ${space}`,
    };
  }
  if (fit.kind !== 'too-tall') return null;
  return {
    severity: 'warning',
    text: `Seite 3: Das Bild ist zu hoch und wurde auf ${fit.scalePct} % verkleinert. ${space}`,
  };
}

/** Ratio as SQUAD's staff would set it in a graphics tool: "1 : 1,11". */
const formatRatio = (heightPx: number) =>
  `1 : ${(heightPx / SQUAD_PAGE3_IMAGE_WIDTH_PX).toFixed(2).replace('.', ',')}`;

/** First line at the upload field: the size to ask the designer for — an estimate, and the most height that fits. */
export function squadPage3OptimalSizeText(area: SquadPage3ImageArea): string {
  const heightPx = squadPage3MaxHeightPx(area);
  if (heightPx < 1) return 'In diesem Factsheet ist unter „Rollierende Wertentwicklung“ kein Platz mehr für ein Bild.';
  return (
    `Optimal für dieses Factsheet: ca. ${SQUAD_PAGE3_IMAGE_WIDTH_PX} × ${heightPx} px ` +
    `(Richtwert; Breite × maximale Höhe, Seitenverhältnis ${formatRatio(heightPx)})`
  );
}

/** Second line at the upload field: why the number changes, and that it is an estimate. */
export const SQUAD_PAGE3_IMAGE_HINT =
  'Die Höhe hängt von diesem Factsheet ab: mehr Kalenderjahr-Zeilen und längere Fußnoten auf Seite 3 ' +
  'lassen weniger Platz. Geschätzt aus den Eingaben – die PDF-Vorschau misst genau. ' +
  'Überschriften und Abstände bitte im Bild vorsehen.';

const formatCm = (pt: number) => `${((pt * 2.54) / 72).toFixed(1).replace('.', ',')} cm`;

/**
 * Lines under the upload field once an image is there, worded for SQUAD's staff.
 * `area` is the estimate, so the numbers carry a "ca.".
 */
export function squadPage3ImageStatus(
  area: SquadPage3ImageArea | null | undefined,
  image: SquadImageSize | null | undefined,
): SquadPage3ImageStatus[] {
  const fit = squadPage3ImageFit(area, image);
  if (!fit || !image) return [];
  const size = `${image.width} × ${image.height} px`;
  const lines: SquadPage3ImageStatus[] = [];
  if (fit.kind === 'no-room') {
    lines.push({ severity: 'warning', text: NO_ROOM_TEXT });
  } else if (fit.kind === 'too-tall') {
    lines.push({
      severity: 'warning',
      text:
        `Zu groß: Das Bild (${size}) ist zu hoch und wird im PDF auf ca. ${fit.scalePct} % verkleinert – ` +
        `dann ist es schmaler als die Spalte. Platz: ${spaceText(fit, 'ca. ')}.`,
    });
  } else if (fit.kind === 'slightly-tall') {
    lines.push({
      severity: 'info',
      text:
        `Knapp: Das Bild (${size}) ist etwas höher als der geschätzte Platz und wird im PDF ggf. leicht ` +
        `verkleinert (auf ca. ${fit.scalePct} %) – bitte in der PDF-Vorschau prüfen. Platz: ${spaceText(fit, 'ca. ')}.`,
    });
  } else if (fit.kind === 'too-short') {
    lines.push({
      severity: 'warning',
      text:
        `Zu niedrig: Das Bild (${size}) füllt die Höhe nicht – darunter bleiben ca. ${formatCm(fit.emptyPt)} ` +
        `Weißraum. Platz: ${spaceText(fit, 'ca. ')}.`,
    });
  } else {
    lines.push({ severity: 'success', text: `Passt: Das Bild (${size}) füllt den Platz bis zur Fußnote.` });
  }
  if (image.width < SQUAD_PAGE3_IMAGE_WIDTH_PX) {
    lines.push({
      severity: 'warning',
      text:
        `Nur ${image.width} px breit: im PDF wird es auf die Spaltenbreite gezogen und kann unscharf wirken. ` +
        `Bitte ${SQUAD_PAGE3_IMAGE_WIDTH_PX} px breit anlegen.`,
    });
  }
  return lines;
}

/** Long enough for a 5 MB upload; a stalled load must not hold anything up. */
const IMAGE_SIZE_TIMEOUT_MS = 8000;

/** Pixel size of an image in the browser; `null` when it does not load in time. */
export function loadSquadImageSize(src?: string): Promise<SquadImageSize | null> {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new window.Image();
    const timer = window.setTimeout(() => resolve(null), IMAGE_SIZE_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img.naturalWidth && img.naturalHeight ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(null);
    };
    img.src = src;
  });
}
